if (!customElements.get('estoria-product-details')) {
  customElements.define('estoria-product-details', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      if (!this.dataset.arranged && !this.arrangeDescription()) return;
      this.connectSlider();
    }
    arrangeDescription() {
      const source = this.querySelector('[data-detail-source]');
      const targets = [...this.querySelectorAll('[data-detail-copy]')];
      if (!source || targets.length !== 3) return false;
      let content = source;
      while (content.children.length === 1 && content.firstElementChild.matches('div, section')) content = content.firstElementChild;
      const nodes = [...content.childNodes];
      const groups = [[]];
      for (const node of nodes) {
        const heading = node.nodeType === 1 && (node.matches('h1, h2, h3, h4, h5, h6') || (node.matches('p') && node.firstElementChild?.matches('strong, b') && node.firstChild === node.firstElementChild));
        if (heading && groups.at(-1).some(item => item.textContent.trim()) && groups.length < 3) groups.push([]);
        groups.at(-1).push(node);
      }
      // Unknown description structures stay intact in the readable fallback.
      if (groups.length !== 3 || groups.some(group => !group.some(node => node.textContent.trim()))) return false;
      groups.forEach((group, index) => targets[index].append(...group));
      source.closest('.estoria-product-details__copy').hidden = true;
      this.dataset.arranged = 'true';
      return true;
    }
    connectSlider() {
      this.viewport = this.querySelector('.estoria-detail-viewport');
      this.track = this.querySelector('.estoria-detail-grid');
      if (!this.viewport || !this.track || !this.dataset.carouselUrl) return;
      this.slides = [...this.track.querySelectorAll('.estoria-detail-row')];
      this.dots = [...this.querySelectorAll('[data-detail-dot]')];
      this.previous = this.querySelector('button[name="previous"]');
      this.next = this.querySelector('button[name="next"]');
      this.counter = this.querySelector('.slider-counter--current');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      const controller = this.controller = new AbortController();
      const options = { signal: controller.signal };
      import(this.dataset.carouselUrl).then(({ default: EmblaCarousel }) => {
        if (!this.isConnected || this.controller !== controller) return;
        // Preserve any swipe made while the carousel module was still loading.
        const nativeIndex = this.slides.reduce((nearest, slide, index) => Math.abs(slide.offsetLeft - this.viewport.scrollLeft) < Math.abs(this.slides[nearest].offsetLeft - this.viewport.scrollLeft) ? index : nearest, 0);
        this.dataset.sliderReady = 'true';
        this.viewport.scrollLeft = 0;
        this.carousel = EmblaCarousel(this.viewport, {
          loop: false,
          align: 'start',
          startIndex: this.selectedIndex ?? nativeIndex,
          watchDrag: (_api, event) => !event.target.closest('a, button, input, textarea, select') && (event.type !== 'mousedown' || !event.target.closest('[data-detail-copy]')),
        });
        this.carousel.on('select', () => this.syncSlides());
        this.carousel.on('reInit', () => this.syncSlides());
        this.previous.addEventListener('click', () => this.goToSlide(this.selectedIndex - 1), options);
        this.next.addEventListener('click', () => this.goToSlide(this.selectedIndex + 1), options);
        this.dots.forEach(dot => dot.addEventListener('click', () => this.goToSlide(Number(dot.dataset.detailDot)), options));
        this.addEventListener('keydown', event => {
          if (event.target !== this.viewport && !event.target.matches('[data-detail-dot]')) return;
          const index = this.selectedIndex ?? 0;
          const destinations = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: this.slides.length - 1 };
          if (!(event.key in destinations)) return;
          event.preventDefault();
          this.goToSlide(destinations[event.key]);
        }, options);
        this.querySelector('[data-detail-controls]').hidden = false;
        this.syncSlides();
        if (typeof ResizeObserver !== 'undefined') {
          this.resizeObserver = new ResizeObserver(() => this.syncHeight());
          this.slides.forEach(slide => this.resizeObserver.observe(slide));
        }
      }).catch(() => {
        if (this.controller !== controller) return;
        this.disconnectedCallback();
        this.querySelector('[data-detail-controls]').hidden = true;
      });
    }
    goToSlide(index) {
      if (!this.carousel) return;
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      this.carousel.scrollTo(target, this.motion.matches);
    }
    syncSlides() {
      this.selectedIndex = this.carousel.selectedScrollSnap();
      this.slides.forEach((slide, index) => {
        const active = index === this.selectedIndex;
        if (!active && slide.contains(this.ownerDocument.activeElement)) this.viewport.focus({ preventScroll: true });
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        this.dots[index]?.setAttribute('aria-current', String(active));
      });
      this.previous.disabled = !this.carousel.canScrollPrev();
      this.next.disabled = !this.carousel.canScrollNext();
      this.counter.textContent = String(this.selectedIndex + 1);
      this.syncHeight();
    }
    syncHeight() {
      const height = Math.ceil(this.slides[this.selectedIndex]?.getBoundingClientRect().height || 0);
      if (!height || height === this.slideHeight) return;
      this.slideHeight = height;
      this.viewport.style.setProperty('--detail-slide-height', `${height}px`);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.carousel?.destroy();
      this.carousel = null;
      delete this.dataset.sliderReady;
      this.slides?.forEach(slide => { slide.inert = false; slide.setAttribute('aria-hidden', 'false'); });
    }
  });
}

if (!customElements.get('estoria-product-value')) {
  customElements.define('estoria-product-value', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      try { this.prices = JSON.parse(this.querySelector('[data-variant-prices]').textContent); }
      catch { return; }
      if (typeof subscribe !== 'function') return;
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const main = this.ownerDocument.querySelector('product-info[data-update-url="true"]');
        if (!main || data.sectionId !== main.dataset.section) return;
        const value = this.prices[data.variant?.id];
        if (!value) return;
        this.querySelector('[data-value-price]').textContent = value.price;
        this.querySelector('[data-value-compare]').textContent = value.compare;
        this.querySelector('[data-compare-route]').hidden = !value.discounted;
      });
    }
    disconnectedCallback() { this.unsubscribe?.(); this.unsubscribe = null; }
  });
}

if (!customElements.get('estoria-product-videos')) {
  customElements.define('estoria-product-videos', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.videos = [...this.querySelectorAll('video')];
      this.visible = new Set();
      this.pausedByUser ??= new WeakSet();
      this.pausedBySection ??= new WeakSet();
      this.autoplaying ??= new WeakSet();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      for (const video of this.videos) {
        video.addEventListener('pause', () => {
          if (this.pausedBySection.has(video)) this.pausedBySection.delete(video);
          else { this.pausedByUser.add(video); this.autoplaying.delete(video); }
        }, options);
        video.addEventListener('play', () => {
          this.pausedByUser.delete(video);
          if (this.ownerDocument.hidden || (this.observer && !this.visible.has(video))) this.stop(video);
        }, options);
      }
      this.ownerDocument.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => this.sync(), options);
      if (typeof IntersectionObserver === 'undefined') return;
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= .5) this.visible.add(entry.target);
          else this.visible.delete(entry.target);
        }
        this.sync();
      }, { threshold: [0, .5] });
      this.videos.forEach(video => this.observer.observe(video));
    }
    stop(video) {
      this.autoplaying.delete(video);
      if (video.paused) return;
      this.pausedBySection.add(video);
      video.pause();
    }
    sync() {
      for (const video of this.videos) {
        if (this.ownerDocument.hidden || (this.observer && !this.visible.has(video))) this.stop(video);
        else if ((this.motion.matches || this.dataset.autoplay !== 'true') && this.autoplaying.has(video)) this.stop(video);
        else if (this.dataset.autoplay === 'true' && !this.motion.matches && !this.pausedByUser.has(video)) {
          if (!this.visible.has(video) || !video.paused) continue;
          this.autoplaying.add(video);
          video.play()?.catch(() => this.autoplaying.delete(video));
        }
      }
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      this.controller?.abort();
      this.videos?.forEach(video => this.stop(video));
    }
  });
}
